import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  throwError,
  withLatestFrom,
} from 'rxjs';

import { EditInfoDialogComponent } from '@fantaz/user-verification/ui/edit-info-dialog';

import { AuthActions, AuthSelectors } from '@fantaz/shared/auth';
import {
  DataAccessAccountService,
  AccountService,
  ProfileApiService,
  VerificationDuplicateException,
  VerificationEscalateException,
  VerificationIncompleteException,
  VerificationRejectedException,
  VerifyUserDto,
} from '@fantaz/shared/data-access/api';
import * as VerifyUserActions from '../verify-user/verify-user.actions';
import * as EditInfoActions from './edit-info.actions';
import { SaveEditInfoActions } from './edit-info.actions';
import * as EditInfoASelectors from './edit-info.selectors';
import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { UploadPictureActions } from '../upload-picture';
import { RegisterCreditCardActions } from '../register-credit-card';
import { VerificationFailureDialogComponent } from '@fantaz/user-verification/ui/verification-failure-dialog';
import { GAEventKeys, User } from '@fantaz/shared/data-access/models';

@Injectable()
export class EditInfoEffects {
  // inject services here
  private actions$: Actions = inject(Actions);
  private dialog: MatDialog = inject(MatDialog);
  private store: Store = inject(Store);
  private accountService: AccountService = inject(DataAccessAccountService);

  /**
   * Effect to open the edit info dialog
   */
  openEditDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditInfoActions.openEditInfoDialog),
      withLatestFrom(
        this.store.select(AuthSelectors.getAuthUser),
        (_, user) => ({
          user,
        }),
      ),
      exhaustMap(({ user }) => {
        const dialogRef = this.dialog.open(EditInfoDialogComponent, {
          data: {
            // TODO: move to observable data
            initialData: {
              firstName: user?.firstName,
              lastName: user?.lastName,
              dateOfBirth: user?.birthday,
              address: user?.legalAddress,
            },
            onSubmit: (data: any) => {
              this.store.dispatch(
                EditInfoActions.saveEditInfo({
                  user: data,
                }),
              );
            },
            submitting$: this.store.select(EditInfoASelectors.isSaving),
            closeSignal$: this.store.select(
              EditInfoASelectors.getEditInfoError,
            ),
          },
        });

        return dialogRef.afterClosed();
      }),
      map((result) => {
        if (result?.source === 'button') {
          return VerifyUserActions.openVerificationDialog({});
        }

        return EditInfoActions.closeEditInfoDialog();
      }),
    ),
  );

  /**
   * Effect to handle save user profile on edit info
   */
  saveUserProfileOnEditInfoEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditInfoActions.saveEditInfo),
      switchMap(({ user }) => {
        return this.accountService
          .verifyAccount(
            new VerifyUserDto({
              ...user,
            }),
          )
          .pipe(
            map(() => {
              return SaveEditInfoActions.saveUserProfileSuccess({
                user,
              });
            }),
            catchError((err) => {
              return [
                SaveEditInfoActions.saveUserProfileFailure({
                  error: err,
                  user,
                }),
              ];
            }),
          );
      }),
    ),
  );

  /**
   * Effect to update user locally on edit info success
   */
  submitEditDialogEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaveEditInfoActions.saveUserProfileSuccess),
      mergeMap(({ user }) => {
        return [
          VerifyUserActions.verifyUserSuccess({ source: 'kyc' }),
          AuthActions.updateUserLocally({
            user: {
              firstName: user.firstName,
              lastName: user.lastName,
              legalAddress: user.address,
              shippingAddress: user.address,
              birthday: user.dateOfBirth,
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to handle edit info profile fails
   */
  editInfoFailuresEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaveEditInfoActions.saveUserProfileFailure),
      switchMap(({ error }) => {
        let attempts;
        let verification_action = 'edit-user-info';
        if (error instanceof VerificationIncompleteException) {
          attempts = error?.attempts;
          verification_action = 'verification-incomplete';
        }
        return [
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.USER_VERIFICATION_STATUS,
              verification_action,
              verification_status: 'user-kyc-verification-fail',
              verification_attempts_failed:
                attempts?.current.CUSTOMER_IDENTITY || 0,
            },
          }),
          EditInfoActions.saveEditInfoFailure({ error }),
          ...(attempts !== null
            ? [
                AuthActions.updateUserLocally({
                  user: {
                    //
                    verificationStatus: {
                      attempts,
                    },
                  },
                }),
              ]
            : []),
        ];
      }),
    ),
  );

  /**
   * Effect to handle incomplete verification
   */
  handleVerifyUserIncompleteFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditInfoActions.saveEditInfoFailure),
      filter(({ error }) => error instanceof VerificationIncompleteException),
      exhaustMap(({ error }) => {
        const dialogRef = this.dialog.open(VerificationFailureDialogComponent, {
          disableClose: true,
          data: {
            ...(error as unknown as VerificationIncompleteException),
          },
        });
        return dialogRef.afterClosed();
      }),
      mergeMap((result: { option?: string; signal?: string; user: User }) => {
        if (result && result.signal === 'edit-personal-info') {
          return [
            EditInfoActions.openEditInfoDialog(),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'option-selected',
                verification_status: 'edit-info-selected',
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current
                    .CUSTOMER_IDENTITY || 0,
              },
            }),
          ];
        }

        if (result && result.signal === 'try-another-method') {
          return [
            VerifyUserActions.openVerificationDialog({}),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'option-selected',
                verification_status: 'try-another-method',
              },
            }),
          ];
        }

        if (result && result.signal === 'register-credit-card') {
          return [
            RegisterCreditCardActions.openRegisterCreditCardDialog({}),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'register-credit-card-selected',
                verification_status: `edit-info-register-credit-card`,
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current.CC || 0,
              },
            }),
          ];
        }
        if (result && result.signal === 'upload-id-picture') {
          return [
            UploadPictureActions.showUploadPictureDialog(),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'edit-info-upload-id-selected',
                verification_status: `edit-info-upload-id-picture`,
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current.DOCUMENT ||
                  0,
              },
            }),
          ];
        }
        if (result && result.signal === 'close-button') {
          return [
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'close-edit-info',
                verification_status: `close-edit-info`,
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current
                    .CUSTOMER_IDENTITY || 0,
              },
            }),
          ];
        }

        return [];
      }),
    ),
  );

  /**
   * Effect to handle duplicate, rejected, and escalate verification
   */
  handleVerifyUserFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditInfoActions.saveEditInfoFailure),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      filter(([{ error }]) =>
        [
          error instanceof VerificationDuplicateException,
          error instanceof VerificationRejectedException,
          error instanceof VerificationEscalateException,
        ].some((v) => v),
      ),
      switchMap(([{ error }, user]) => {
        if (error instanceof VerificationEscalateException) {
          return [
            VerifyUserActions.verifyUserFailure({
              error,
            }),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'verification-escalate',
                verification_status: 'user-verified-fail',
                verification_attempts_failed:
                  user?.verificationStatus?.attempts?.current.CC || 0,
              },
            }),
          ];
        }
        return [
          VerifyUserActions.verifyUserFailure({
            error,
          }),
        ];
      }),
    ),
  );
}
